<script>
import { getStorageRequestDetail, updateStorageStatus } from '@/api/storage.js'
import { getAdminInfo } from '@/utils/token.js'

export default {
  name: 'StorageRequestDetailPage',
  data() {
    return {
      loading: false,
      savingCode: '',
      items: [],
      memo: {},
      admin: getAdminInfo(),
    }
  },
  computed: {
    requestCode() {
      return this.$route.params.storageRequestCode
    },
    head() {
      return this.items[0] || {}
    },
    memberLabel() {
      const name = this.head.userName || '-'
      const email = this.head.userEmail || '-'
      const phone = this.head.userPhone || '-'
      return `${name} / ${email} / ${phone}`
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    async load() {
      this.loading = true
      try {
        const { data } = await getStorageRequestDetail(this.requestCode)
        if (data.code === 0) {
          this.items = data.items || []
          this.memo = Object.fromEntries(this.items.map(item => [item.storageItemCode, '']))
        }
      } finally {
        this.loading = false
      }
    },
    fmtDate(value) {
      return value ? String(value).slice(0, 10) : '-'
    },
    fmtDt(value) {
      return value ? String(value).slice(0, 16).replace('T', ' ') : '-'
    },
    fmtWon(value) {
      return `${Number(value || 0).toLocaleString('ko-KR')}원`
    },
    statusLabel(value) {
      return {
        STORED: '보관중',
        PICKUP_REQUESTED: '수령신청',
        DELIVERY_REQUESTED: '배송신청',
        TRANSFERRING: '이동중',
        READY_FOR_PICKUP: '수령준비완료',
        PICKED_UP: '수령완료',
        SHIPPING: '배송중',
        DELIVERED: '배송완료',
        CANCELED: '취소',
      }[value] || value || '-'
    },
    paymentLabel(value) {
      return value === 'CASH' ? '계좌이체' : value === 'CARD' ? '카드결제' : value || '-'
    },
    requestTypeLabel(value) {
      return value === 'DELIVERY' ? '배송신청' : value === 'PICKUP' ? '방문수령 신청' : '-'
    },
    canMarkTransferring(item) {
      if (!item.fromBranchCode || item.fromBranchCode === item.toBranchCode) return false
      if (this.admin.adminType !== 'HQ' && item.fromBranchCode !== this.admin.branchCode) return false
      return item.transferStatus !== 'SHIPPED' && item.transferStatus !== 'ARRIVED'
    },
    canMarkArrived(item) {
      if (!item.toBranchCode || item.fromBranchCode === item.toBranchCode) return false
      if (this.admin.adminType !== 'HQ' && item.toBranchCode !== this.admin.branchCode) return false
      return item.storageStatus === 'TRANSFERRING' && item.transferStatus === 'SHIPPED'
    },
    async markTransferring(item) {
      this.savingCode = item.storageItemCode
      try {
        const { data } = await updateStorageStatus(item.storageItemCode, {
          storageStatus: 'TRANSFERRING',
          memo: this.memo[item.storageItemCode] || '방문 수령 지점으로 제품 이동을 시작했습니다.',
        })
        if (data.code !== 0) {
          window.alert(data.message || '이동중 처리에 실패했습니다.')
          return
        }
        await this.load()
      } catch (error) {
        window.alert(error.response?.data?.message || '이동중 처리 중 오류가 발생했습니다.')
      } finally {
        this.savingCode = ''
      }
    },
    async markArrived(item) {
      const nextStatus = item.requestType === 'DELIVERY' ? 'SHIPPING' : 'READY_FOR_PICKUP'
      const defaultMemo = item.requestType === 'DELIVERY'
        ? '배송 지점에서 제품을 수령하고 배송을 시작했습니다.'
        : '방문 수령 지점에서 제품을 수령했습니다.'
      this.savingCode = item.storageItemCode
      try {
        const { data } = await updateStorageStatus(item.storageItemCode, {
          storageStatus: nextStatus,
          memo: this.memo[item.storageItemCode] || defaultMemo,
        })
        if (data.code !== 0) {
          window.alert(data.message || '지점 수령완료 처리에 실패했습니다.')
          return
        }
        await this.load()
      } catch (error) {
        window.alert(error.response?.data?.message || '지점 수령완료 처리 중 오류가 발생했습니다.')
      } finally {
        this.savingCode = ''
      }
    },
  },
}
</script>

<template>
  <div class="storage-detail-page">
    <div class="page-header">
      <div>
        <h1>보관/이동 상세</h1>
        <p>방문 수령 또는 배송을 위해 이동/처리해야 할 제품과 회원 정보를 확인합니다.</p>
      </div>
      <div class="header-actions">
        <v-btn variant="text" to="/storage">목록</v-btn>
        <v-btn color="primary" variant="tonal" :loading="loading" @click="load">새로고침</v-btn>
      </div>
    </div>

    <v-progress-linear v-if="loading" indeterminate color="primary" height="3" class="mb-4" />

    <v-card v-if="items.length" class="info-card mb-5" rounded="lg">
      <div class="info-grid">
        <div>
          <span>신청번호</span>
          <strong>{{ requestCode }}</strong>
        </div>
        <div>
          <span>신청유형</span>
          <strong>{{ requestTypeLabel(head.requestType) }}</strong>
        </div>
        <div>
          <span>회원</span>
          <strong>{{ memberLabel }}</strong>
        </div>
        <div>
          <span>방문 수령 지점</span>
          <strong>{{ head.targetBranchName || '-' }}</strong>
        </div>
        <div>
          <span>예약일</span>
          <strong>{{ fmtDate(head.expectedReceiptDate) }}</strong>
        </div>
      </div>
    </v-card>

    <v-card rounded="lg">
      <v-card-title class="text-subtitle-1 font-weight-bold">이동/처리 대상 제품</v-card-title>
      <v-table density="comfortable">
        <thead>
          <tr>
            <th>상태</th>
            <th>상품</th>
            <th>회원/주문</th>
            <th>결제</th>
            <th>이동</th>
            <th class="text-end">금액</th>
            <th>처리</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in items" :key="item.storageItemCode">
            <td>
              <v-chip size="small" color="primary" variant="tonal">{{ statusLabel(item.storageStatus) }}</v-chip>
              <div class="text-caption text-grey mt-1">{{ item.transferStatus || '-' }}</div>
            </td>
            <td>
              <div class="font-weight-bold">{{ item.productName }}</div>
              <div class="text-caption text-grey">주문상품 {{ item.orderItemCode }}</div>
            </td>
            <td>
              <div>{{ item.userName }} / {{ item.userEmail }}</div>
              <div class="text-caption text-grey">주문 {{ item.orderCode }}</div>
              <div class="text-caption text-grey">주문일 {{ fmtDt(item.orderedDt) }}</div>
            </td>
            <td>
              <div>{{ paymentLabel(item.paymentType) }} / {{ item.paymentStatus || '-' }}</div>
              <div class="text-caption text-grey">결제일 {{ fmtDt(item.paidDt) }}</div>
            </td>
            <td>
              <div>{{ item.fromBranchName || item.currentBranchName || '-' }} → {{ item.toBranchName || item.targetBranchName || '-' }}</div>
              <div class="text-caption text-grey">출발 {{ fmtDt(item.shippedDt) }} / 도착 {{ fmtDt(item.arrivedDt) }}</div>
            </td>
            <td class="text-end">{{ fmtWon(item.itemAmount) }}</td>
            <td class="action-cell">
              <div v-if="canMarkTransferring(item) || canMarkArrived(item)" class="action-form">
                <v-text-field
                  v-model="memo[item.storageItemCode]"
                  density="compact"
                  variant="outlined"
                  hide-details
                  placeholder="처리 메모"
                />
                <v-btn
                  v-if="canMarkTransferring(item)"
                  size="small"
                  color="warning"
                  :loading="savingCode === item.storageItemCode"
                  @click="markTransferring(item)"
                >
                  이동중 처리
                </v-btn>
                <v-btn
                  v-else
                  size="small"
                  color="primary"
                  :loading="savingCode === item.storageItemCode"
                  @click="markArrived(item)"
                >
                  {{ item.requestType === 'DELIVERY' ? '배송중 처리' : '지점 수령완료' }}
                </v-btn>
              </div>
              <span v-else class="text-caption text-grey">처리할 이동 요청 없음</span>
            </td>
          </tr>
          <tr v-if="!items.length">
            <td colspan="7" class="text-center text-grey py-8">조회 가능한 보관 신청 정보가 없습니다.</td>
          </tr>
        </tbody>
      </v-table>
    </v-card>
  </div>
</template>

<style scoped>
.page-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
}

.page-header h1 {
  margin: 0;
  color: #fff;
  font-size: 24px;
  font-weight: 800;
}

.page-header p {
  margin: 6px 0 0;
  color: #aeb8c8;
  font-size: 14px;
}

.header-actions {
  display: flex;
  gap: 8px;
}

.info-card {
  padding: 20px;
  background: #1f2633;
  border: 1px solid #333c4d;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 16px;
}

.info-grid span {
  display: block;
  color: #9ba7b9;
  font-size: 12px;
}

.info-grid strong {
  display: block;
  margin-top: 6px;
  color: #fff;
  font-size: 14px;
}

.action-cell {
  min-width: 300px;
}

.action-form {
  display: grid;
  grid-template-columns: 1fr 100px;
  gap: 8px;
  align-items: center;
}

@media (max-width: 1200px) {
  .info-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
